// "Apply to all …" — bulk-attach one Storefront Style to every element of its kind across the
// storefront's page-config blobs, plus the config-scoped lookup the pencil pickers use.
//
// Why this file exists alongside stylesConfig.ts:
//  * `STYLE_TARGET_BY_KEY` (stylesConfig.ts) remains the SINGLE source of truth for what element
//    type and part a key represents. Nothing here re-declares that.
//  * What it can't say is *membership*: which config blob owns which keys, and whether a key of a
//    given name is really that element role in that blob. `title` means "product-card name" in
//    homePageConfig/searchPageConfig but "page heading" in cartPageConfig; `image` means "card
//    image" on the search page but "hero image" on the product page. So each config gets an
//    explicit allow-list of its genuinely style-role keys, and the element type/part for each of
//    those still comes from the one table.
//  * It is admin-only (it knows about IStorefrontSettings config fields), so it stays out of
//    stylesConfig.ts, which is the hand-mirror of the shopper renderer's helper.
//
// Section instances (the five StyleProps dictionaries on IStorefrontSection) are deliberately OUT
// of scope for bulk apply — those are per-section decisions, and the section editor has its own
// pickers. Configs only.

import { IStorefrontSettings, IStorefrontStyle, StorefrontStyleElementType } from '../../../models/Storefront';
import { IStyleTarget, STYLE_REF_KEY, styleTargetForKey } from './stylesConfig';

/** The `IStorefrontSettings` fields that hold a page-presentation config blob with style keys. */
export type StyleConfigField =
    | 'headerConfig'
    | 'homePageConfig'
    | 'searchPageConfig'
    | 'cartPageConfig'
    | 'checkoutPageConfig'
    | 'productPageConfig'
    | 'bundlePageConfig';

interface IStyleConfigTarget {
    configField: StyleConfigField;
    /** Shown in the confirm dialog ("Home page — 5 elements"). */
    label: string;
    /** Top-level keys of this config that genuinely carry a style role (see the note above). */
    keys: readonly string[];
}

export const STYLE_CONFIG_TARGETS: readonly IStyleConfigTarget[] = [
    // Header: the "Menu" dropdown trigger is a button; the keyword box is a form input. `title`,
    // `logo`, `bar`, `menu*` are chrome, not element-style roles.
    { configField: 'headerConfig', label: 'Header', keys: ['menuButton', 'searchBar'] },
    // Home: card / title / price / image ARE the product-card parts here (see the scoped stylesheet
    // in StorefrontHomePageEditor — .storefront-product-card-name is homeConfig.title).
    { configField: 'homePageConfig', label: 'Home page', keys: ['card', 'title', 'price', 'image', 'cardHover', 'addToCartButton', 'scrollButton', 'moreLikeThisButton'] },
    { configField: 'searchPageConfig', label: 'Search page', keys: ['card', 'title', 'price', 'image', 'cardHover', 'addToCartButton', 'addToCartLabel', 'searchBar'] },
    // Cart/checkout/product/bundle: `title`/`image`/`container` are page chrome, NOT card parts —
    // excluded on purpose so a "product card" style never lands on a page heading.
    { configField: 'cartPageConfig', label: 'Cart page', keys: ['checkoutButton', 'secondaryButton', 'quantityInput'] },
    { configField: 'checkoutPageConfig', label: 'Checkout page', keys: ['placeOrderButton', 'continueButton'] },
    { configField: 'productPageConfig', label: 'Product page', keys: ['addToCartButton', 'addToCartLabel', 'scrollButton', 'linkedCard'] },
    { configField: 'bundlePageConfig', label: 'Bundle page', keys: ['addToCartButton', 'addToCartLabel'] },
];

const targetsByField = new Map<StyleConfigField, IStyleConfigTarget>(
    STYLE_CONFIG_TARGETS.map((t) => [t.configField, t]));

/**
 * The (elementType, part) a picker on `key` should read/write **within `configField`** — i.e.
 * `styleTargetForKey` narrowed by that config's allow-list, so the cart page's `title` doesn't
 * offer product-card styles. Returns undefined for keys that carry no style role in this config.
 */
export function styleTargetForConfigKey(configField: StyleConfigField, key?: string | null): IStyleTarget | undefined {
    if (!key) return undefined;
    const target = targetsByField.get(configField);
    if (!target || !target.keys.includes(key)) return undefined;
    return styleTargetForKey(key);
}

/** The part implied by `key` within `configField` (see styleTargetForConfigKey). */
export function stylePartForConfigKey(configField: StyleConfigField, key?: string | null): string | undefined {
    return styleTargetForConfigKey(configField, key)?.part;
}

/** The allow-listed keys of one config that belong to `elementType`. */
function matchingKeys(target: IStyleConfigTarget, elementType: StorefrontStyleElementType): string[] {
    return target.keys.filter((key) => styleTargetForKey(key)?.elementType === elementType);
}

export interface IStyleBulkApplyPlanEntry {
    configField: StyleConfigField;
    label: string;
    keys: string[];
}

/**
 * What "Apply to all" would touch for this element type, per config — used to spell the confirm
 * dialog out rather than making the merchant guess. Empty list ⇒ nothing to apply (section and
 * typography styles have no config-level keys today), so the menu item should be hidden.
 */
export function planStyleBulkApply(elementType: StorefrontStyleElementType): IStyleBulkApplyPlanEntry[] {
    return STYLE_CONFIG_TARGETS
        .map((target) => ({ configField: target.configField, label: target.label, keys: matchingKeys(target, elementType) }))
        .filter((entry) => entry.keys.length > 0);
}

/** Total elements a bulk apply would target (the dialog's headline number). */
export function planStyleBulkApplyCount(elementType: StorefrontStyleElementType): number {
    return planStyleBulkApply(elementType).reduce((sum, entry) => sum + entry.keys.length, 0);
}

/**
 * Write `$styleRef: style.id` into every matching key of every page-config blob on `settings`,
 * returning a NEW settings object plus the number of keys actually changed.
 *
 *  * Existing local overrides are preserved — only the `$styleRef` entry is set/overwritten.
 *  * A key with no dict yet is created as `{ $styleRef }`.
 *  * A key already pointing at this style is left alone (and not counted).
 *  * A config blob that isn't valid JSON is skipped rather than clobbered.
 *
 * The caller feeds the result back into the pages editor's working settings state, so the change
 * rides the normal Edit-Mode draft and persists on Exit Edit Mode like any other config edit.
 */
export function applyStyleToAll(
    settings: IStorefrontSettings,
    style: IStorefrontStyle,
): { settings: IStorefrontSettings; count: number } {
    const next: IStorefrontSettings = { ...settings };
    let count = 0;

    for (const target of STYLE_CONFIG_TARGETS) {
        const keys = matchingKeys(target, style.elementType);
        if (keys.length === 0) continue;

        const raw = settings[target.configField];
        let config: Record<string, unknown>;
        if (!raw) {
            config = {};
        } else {
            try {
                const parsed = JSON.parse(raw) as unknown;
                if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) continue;
                config = { ...(parsed as Record<string, unknown>) };
            } catch {
                // Hand-edited raw JSON that no longer parses — leave it exactly as it is.
                continue;
            }
        }

        let changed = false;
        for (const key of keys) {
            const existing = config[key];
            const dict = (existing && typeof existing === 'object' && !Array.isArray(existing))
                ? (existing as Record<string, unknown>)
                : {};
            if (dict[STYLE_REF_KEY] === style.id) continue;
            config[key] = { ...dict, [STYLE_REF_KEY]: style.id };
            changed = true;
            count++;
        }
        if (changed) next[target.configField] = JSON.stringify(config);
    }

    return { settings: next, count };
}
